import React, { useState, useEffect } from 'react';
import '../../styles/VentingRoomDashboard.css'; // Import the CSS file for styling
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa'; // For internal news navigation
import { BsCameraVideo, BsImage, BsFileEarmarkText, BsMusicNote } from 'react-icons/bs'; // Icons for different media types

// Sample trending news items (replace with actual data)
// This array now holds ALL trending media items, regardless of section or type.
const allTrendingMediaItems = [
  { id: 1, title: 'Community Issues (Image 1).', section: 'social-injustice', type: 'image', mediaUrl: 'https://placehold.co/400x250/E0E0E0/333333?text=Social+Justice+Image+1', likes: 15 },
  { id: 2, title: 'Community Issues (Image 2).', section: 'social-injustice', type: 'image', mediaUrl: 'https://placehold.co/400x250/D0D0D0/333333?text=Social+Justice+Image+2', likes: 18 },
  { id: 3, title: 'Community Issues (Video 1).', section: 'social-injustice', type: 'video', mediaUrl: 'https://www.w3schools.com/html/mov_bbb.mp4', likes: 20 },

  { id: 4, title: 'Global Market Fluctuations (Video 1).', section: 'economic-hell', type: 'video', mediaUrl: 'https://sample-videos.com/video123/mp4/720/big_buck_bunny_720p_1mb.mp4', likes: 28 },
  { id: 5, title: 'Global Market Fluctuations (Image 1).', section: 'economic-hell', type: 'image', mediaUrl: 'https://placehold.co/400x250/C0C0C0/333333?text=Economy+Image+1', likes: 30 },
  { id: 6, title: 'Global Market Fluctuations (Document 1).', section: 'economic-hell', type: 'doc', mediaUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf', likes: 32 },

  { id: 7, title: 'Sustainable Living (Image 1).', section: 'ecosystem+', type: 'image', mediaUrl: 'https://placehold.co/400x250/B0B0B0/333333?text=Ecosystem+Image+1', likes: 42 },
  { id: 8, title: 'Sustainable Living (Audio 1).', section: 'ecosystem+', type: 'audio', mediaUrl: 'https://www.w3schools.com/html/horse.mp3', likes: 45 },
  { id: 9, title: 'Sustainable Living (Image 2).', section: 'ecosystem+', type: 'image', mediaUrl: 'https://placehold.co/400x250/A0A0A0/333333?text=Ecosystem+Image+2', likes: 40 },

  { id: 10, title: 'Modern Relationships (Image 1).', section: 'romantic-chaos', type: 'image', mediaUrl: 'https://placehold.co/400x250/909090/333333?text=Romance+Image+1', likes: 10 },
  { id: 11, title: 'Modern Relationships (Video 1).', section: 'romantic-chaos', type: 'video', mediaUrl: 'https://test-videos.co.uk/vids/bigbuckbunny/mp4/720/Big_Buck_Bunny_720_10s_1MB.mp4', likes: 12 },

  { id: 12, title: 'Emergency Response (Image 1).', section: 'emergency', type: 'image', mediaUrl: 'https://placehold.co/400x250/808080/333333?text=Emergency+Image+1', likes: 55 },
  { id: 13, title: 'Emergency Response (Document 1).', section: 'emergency', type: 'doc', mediaUrl: 'https://www.africau.edu/images/default/sample.pdf', likes: 58 },
];

// Helper component for rendering different media types
const renderMedia = (mediaItem) => {
  switch (mediaItem.type) {
    case 'image':
      return (
        <img
          src={mediaItem.mediaUrl}
          alt="News Media"
          className="news-media news-image"
          onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/400x250/cccccc/333333?text=Image+Error'; }}
        />
      );
    case 'video':
      return (
        <video controls className="news-media news-video">
          <source src={mediaItem.mediaUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    case 'doc':
      return (
        <div className="news-media news-doc">
          <BsFileEarmarkText size={80} color="#ff9800" />
          <p className="media-placeholder-text">Document Preview</p>
          <a href={mediaItem.mediaUrl} target="_blank" rel="noopener noreferrer" className="download-link">Download Document</a>
        </div>
      );
    case 'audio':
      return (
        <div className="news-media news-audio">
          <BsMusicNote size={80} color="#9c27b0" />
          <p className="media-placeholder-text">Audio Player</p>
          <audio controls src={mediaItem.mediaUrl}>
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    default:
      return <p>Unsupported media type.</p>;
  }
};

// New MediaTypeCard component for each media type's slideshow
function MediaTypeCard({ type, items, sectionFilter }) {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  // State to track liked status for each item by ID
  // Using a Set for efficient lookup of liked item IDs
  const [likedItemIds, setLikedItemIds] = useState(new Set());

  // Filter items based on the overall section filter
  const filteredTypeItems = sectionFilter === 'all'
    ? items
    : items.filter(item => item.section === sectionFilter);

  const goToPreviousMedia = () => {
    setCurrentMediaIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  const goToNextMedia = () => {
    setCurrentMediaIndex((prevIndex) => Math.min(prevIndex + 1, filteredTypeItems.length - 1));
  };

  // Reset index when filtered items change (e.g., when section filter changes)
  // No need to reset liked status here, as it's tied to item ID
  useEffect(() => {
    setCurrentMediaIndex(0);
  }, [sectionFilter, filteredTypeItems.length]);

  const currentMedia = filteredTypeItems[currentMediaIndex];

  // Handle like button click
  const handleLikeClick = (itemId) => {
    setLikedItemIds(prevLikedItemIds => {
      const newLikedItemIds = new Set(prevLikedItemIds);
      if (newLikedItemIds.has(itemId)) {
        newLikedItemIds.delete(itemId); // Unlike
        console.log(`Item ${itemId} unliked.`);
      } else {
        newLikedItemIds.add(itemId); // Like
        console.log(`Item ${itemId} liked.`);
      }
      // In a real application, you would send this like/unlike action to your backend
      // and update the total likes count in the database.
      return newLikedItemIds;
    });
  };

  // Get display title for the card
  const getCardTitle = (mediaType) => {
    switch (mediaType) {
      case 'image': return 'Trending Images';
      case 'video': return 'Trending Videos';
      case 'doc': return 'Trending Documents';
      case 'audio': return 'Trending Audio';
      default: return 'Trending Content';
    }
  };

  // Get icon for the card title
  const getCardIcon = (mediaType) => {
    switch (mediaType) {
      case 'image': return <BsImage size={24} />;
      case 'video': return <BsCameraVideo size={24} />;
      case 'doc': return <BsFileEarmarkText size={24} />;
      case 'audio': return <BsMusicNote size={24} />;
      default: return null;
    }
  };


  return (
    <div className="media-type-card">
      <h3 className="media-type-card-title">
        {getCardIcon(type)} {getCardTitle(type)}
      </h3>
      {filteredTypeItems.length > 0 ? (
        <div className="media-type-slideshow-content">
          {filteredTypeItems.length > 1 && (
            <button className="media-navigation left" onClick={goToPreviousMedia} disabled={currentMediaIndex === 0}>
              <FaArrowLeft />
            </button>
          )}

          <div className="media-display-area">
            {currentMedia && renderMedia(currentMedia)}
          </div>

          {filteredTypeItems.length > 1 && (
            <button className="media-navigation right" onClick={goToNextMedia} disabled={currentMediaIndex === filteredTypeItems.length - 1}>
              <FaArrowRight />
            </button>
          )}

          {filteredTypeItems.length > 1 && (
            <div className="media-indicator">
              {filteredTypeItems.map((_, index) => (
                <span key={index} className={index === currentMediaIndex ? 'active' : ''}></span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="no-media-message">No {type}s found for this section.</p>
      )}

      {currentMedia && ( // Only show footer if there's content
        <div className="news-card-footer">
          <p className="media-caption">{currentMedia.title}</p>
          <div className="news-item-actions">
            <button
              className={`like-button ${likedItemIds.has(currentMedia.id) ? 'liked' : ''}`}
              onClick={() => handleLikeClick(currentMedia.id)}
            >
              {likedItemIds.has(currentMedia.id) ? 'Liked' : 'Like'} ({currentMedia.likes})
            </button>
            <span className="more-actions">...</span>
          </div>
        </div>
      )}
    </div>
  );
}


function TrendingNewsContent({ onBack }) {
  const [selectedSection, setSelectedSection] = useState('all'); // State for selected filter

  const handleSectionChange = (event) => {
    setSelectedSection(event.target.value);
  };

  // Function to get the display name for the selected section
  const getSectionDisplayName = (sectionId) => {
    switch (sectionId) {
      case 'all': return 'All News';
      case 'social-injustice': return 'Social Injustice';
      case 'economic-hell': return 'Economic Hell';
      case 'romantic-chaos': return 'Romantic Chaos';
      case 'ecosystem+': return 'Ecosystem +';
      case 'emergency': return 'Emergency';
      default: return 'Trending News';
    }
  };

  // Filter all media items by the selected section
  const filteredBySectionItems = selectedSection === 'all'
    ? allTrendingMediaItems
    : allTrendingMediaItems.filter(item => item.section === selectedSection);

  // Group filtered items by type for each MediaTypeCard
  const images = filteredBySectionItems.filter(item => item.type === 'image');
  const videos = filteredBySectionItems.filter(item => item.type === 'video');
  const docs = filteredBySectionItems.filter(item => item.type === 'doc');
  const audios = filteredBySectionItems.filter(item => item.type === 'audio');


  return (
    <div className="trending-news-container">
      <div className="trending-header">
        <button onClick={onBack} className="back-button"><FaArrowLeft /></button>
        <h2>TRENDING NEWS</h2>
      </div>

      <div className="all-news-section">
        <div className="news-filter-header">
          <h3>{getSectionDisplayName(selectedSection)}</h3>
          <select value={selectedSection} onChange={handleSectionChange} className="section-filter-dropdown">
            <option value="all">All News</option>
            <option value="social-injustice">Social Injustice</option>
            <option value="economic-hell">Economic Hell</option>
            <option value="romantic-chaos">Romantic Chaos</option>
            <option value="ecosystem+">Ecosystem +</option>
            <option value="emergency">Emergency</option>
          </select>
        </div>

        {/* Container for horizontally scrollable media type cards */}
        <div className="news-list-horizontal">
          <MediaTypeCard type="image" items={images} sectionFilter={selectedSection} />
          <MediaTypeCard type="video" items={videos} sectionFilter={selectedSection} />
          <MediaTypeCard type="doc" items={docs} sectionFilter={selectedSection} />
          <MediaTypeCard type="audio" items={audios} sectionFilter={selectedSection} />
        </div>
      </div>
    </div>
  );
}

export default TrendingNewsContent;
